import { useCallback, useState, useContext, useEffect, useMemo } from "react";
import "./MyHotels.scss";
import { AuthContext } from "../../../contexts/AuthContext";
import PageTitle from "../../../components/PageTitle/PageTitle";
import AlertModal from "../../../components/AlertModal/AlertModal";
import Button from "../../../components/Button/Button";
import CiMapPint, { CiMapPin } from "react-icons/ci";
import { CiEdit } from "react-icons/ci";
import { resolveUrl } from "../../../utils/resolveUrl";
import { Modal } from "../../../components/Modal/Modal";
import {
  DynamicFileInput,
  DynamicImagePreview,
} from "../../../components/Inputs/Inputs";
import { Empty } from "../../../components/Empty/Empty";

export default function MyHotels() {
  const {
    axiosAuth,
    currentUser,
    accessibleGroups = [],
    accessibleHotels = [],
  } = useContext(AuthContext);

  const role = currentUser?.role;
  const isGroupAdmin = role === "group_admin";
  const isHotelAdmin = role === "hotel_admin";

  // group_admin : primeiro grupo do array
  const groupId = useMemo(
    () =>
      isGroupAdmin && accessibleGroups.length ? accessibleGroups[0].id : null,
    [isGroupAdmin, accessibleGroups]
  );

  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);

  // modal para edição da imagem
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState("");
  const [saving, setSaving] = useState(false);

  // erros
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);
  const closeError = useCallback(
    () => setErrorModal({ open: false, title: "", message: "" }),
    []
  );

  // carregar hotéis consoante perfil
  useEffect(() => {
    setHotels([]);
    if (!role) return;

    (async () => {
      try {
        setLoading(true);
        if (isGroupAdmin) {
          if (!groupId) return;
          const { data } = await axiosAuth.get(`/hotels/group/${groupId}`);
          setHotels(Array.isArray(data) ? data : []);
        } else if (isHotelAdmin) {
          setHotels(Array.isArray(accessibleHotels) ? accessibleHotels : []);
        } else {
          setHotels(Array.isArray(accessibleHotels) ? accessibleHotels : []);
        }
      } catch (err) {
        openError(
          "Erro ao carregar hotéis",
          err?.response?.data?.detail || err.message
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [
    role,
    groupId,
    isGroupAdmin,
    isHotelAdmin,
    axiosAuth,
    accessibleHotels,
    openError,
  ]);

  // abrir modal
  const onOpenEdit = useCallback((hotel) => {
    setEditingHotel(hotel);
    setNewImageFile(null);
    setNewImagePreview(hotel?.img_url ? resolveUrl(hotel.img_url) : "");
    setIsEditModalVisible(true);
  }, []);

  // fechar modal
  const onCloseEdit = useCallback(() => {
    setIsEditModalVisible(false);
    setEditingHotel(null);
    setNewImageFile(null);
    setNewImagePreview("");
  }, []);

  // selecionar nova imagem
  const onFileChange = useCallback((e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setNewImageFile(file);
    setNewImagePreview(URL.createObjectURL(file));
  }, []);

  // guardar imagem (PUT multipart)
  const onSaveImage = useCallback(async () => {
    if (!editingHotel) return;
    if (!newImageFile) {
      openError("Validação", "Selecione uma imagem para guardar.");
      return;
    }
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append("image", newImageFile);

      const { data } = await axiosAuth.put(`/hotels/${editingHotel.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // atualizar lista local
      setHotels((prev) =>
        prev.map((h) =>
          h.id === editingHotel.id ? { ...h, img_url: data.img_url } : h
        )
      );
      onCloseEdit();
    } catch (err) {
      openError(
        "Erro ao atualizar imagem",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
    }
  }, [editingHotel, newImageFile, axiosAuth, onCloseEdit, openError]);

  return (
    <div className="my-hotels">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}

      <PageTitle title="Os meus hotéis" />

      <div className="container">
        {!loading && hotels.length === 0 && (
          <Empty empty_message="Sem hotéis para apresentar" />
        )}

        {!loading &&
          hotels.map((h) => {
            const addressParts = [
              [h.street, h.number].filter(Boolean).join(" "),
              [h.postal_code, h.city].filter(Boolean).join(" "),
              h.country,
            ].filter(Boolean);
            return (
              <div className="hotel-card" key={h.id}>
                <div className="image">
                  <img
                    src={resolveUrl(h.img_url)}
                    alt={`Imagem do ${h.name}`}
                  />
                </div>
                <div className="content">
                  <div className="content-header">
                    <h3 className="name">{h.name}</h3>
                    {h.hotel_slug && (
                      <span className="slug">{h.hotel_slug}</span>
                    )}
                  </div>
                  {h.location && (
                    <div className="location">
                      <CiMapPin size={14} />
                      <p>{h.location}</p>
                    </div>
                  )}
                  {addressParts.length > 0 && (
                    <p className="address">{addressParts.join(" · ")}</p>
                  )}
                  <div className="actions">
                    <div
                      className="button"
                      type="button"
                      onClick={() => onOpenEdit(h)}
                    >
                      <button>Editar</button>
                      <span>
                        <CiEdit size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      <Modal
        isVisible={isEditModalVisible}
        onClose={onCloseEdit}
        title={`Editar imagem`}
        saveText="Guardar"
        onSave={onSaveImage}
        disableActions={saving}
      >
        <div className="row">
          <DynamicImagePreview imageSrc={newImagePreview} />
        </div>
        <div className="row">
          <DynamicFileInput
            label="Nova imagem"
            name="newImage"
            onChange={onFileChange}
          />
        </div>
        <div></div>
      </Modal>
    </div>
  );
}
